import React, { useState, useEffect, useContext } from "react";
import { NavLink } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { fetchPermissions } from "../utils/checkPermissions";
import Spinner from "../Spinner";
import { useNavigate } from "react-router-dom";

// HCM Sidebar Links
const HCMNavbarLinks = [
  { resource: "analytics", path: "/dashboard/analytics", title: "Analytics" },
  { resource: "applicants", path: "/dashboard/applicants", title: "Applicants" },
  { resource: "benefits", path: "/dashboard/benefits", title: "Benefits" },
  { resource: "employee-development", path: "/dashboard/employee-development", title: "Employee Development" },
  { resource: "employee-management", path: "/dashboard/employee-management", title: "Employee Management" },
  { resource: "enterprise", path: "/dashboard/enterprise", title: "Enterprise" },
  { resource: "hr-services", path: "/dashboard/hr-services", title: "HR Services" },
  { resource: "interviews", path: "/dashboard/interviews", title: "Interviews" },
  { resource: "jobs", path: "/dashboard/jobs", title: "Jobs" },
  { resource: "learning-and-development", path: "/dashboard/learning-and-development", title: "Learning and Development" },
  { resource: "messaging", path: "/dashboard/messaging", title: "Messaging" },
  { resource: "payroll", path: "/dashboard/payroll", title: "Payroll" },
  { resource: "performance-management", path: "/dashboard/performance-management", title: "Performance Management" },
  { resource: "recruiter", path: "/dashboard/recruiter", title: "Recruiter" },
  { resource: "roles", path: "/dashboard/roles", title: "Roles" },
  { resource: "settings", path: "/dashboard/settings", title: "Settings" },
  { resource: "time-and-absence", path: "/dashboard/time-and-absence", title: "Time and Absence" },
  { resource: "users", path: "/dashboard/users", title: "Users" },
  { resource: "work-management", path: "/dashboard/work-management", title: "Work Management" },
  { resource: "subscription", path: "/dashboard/subscription", title: "Subscriptions" },
  { resource: "forms", path: "/dashboard/forms", title: "Forms" },
  { resource: "integrations", path: "/dashboard/integrations", title: "Integrations" }
];

const DashboardSidebar = () => {
  const { user } = useContext(AuthContext);
  const [visibleRoutes, setVisibleRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginType, setLoginType] = useState("employer");
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Retrieve loginType from localStorage or context when Header is loaded
  useEffect(() => {
    const storedLoginType = localStorage.getItem("loginType");
    if (storedLoginType) {
      setLoginType(storedLoginType);
    }
  }, []);

  const setErrorMessage = (message) => {
    setError(message);
  };

  useEffect(() => {
    if (user) {
      const fetchPermissionsData = async () => {
        try {
          const validRoutes = [];
          for (const route of HCMNavbarLinks) {
            const hasPermission = await fetchPermissions(
              user.role,
              route.resource,
              "view",
              loginType,
              setErrorMessage,
              navigate
            );

            if (hasPermission) {
              validRoutes.push(route); // Only add valid routes
            }
          }
          setVisibleRoutes(validRoutes); // Set the visible routes based on permission
        } catch (err) {
          setError("Failed to fetch permissions.");
        } finally {
          setLoading(false); // Stop loading once all permissions are fetched
        }
      };

      fetchPermissionsData();
    } else {
      setLoading(false);
    }
  }, [user]);

  return (
    <aside
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      style={{
        width: isOpen ? "200px" : "60px", // Expand or collapse based on hover
        height: "calc(100vh - 60px)", // Start below the header (adjust 60px to match header height)
        backgroundColor: "#f8f9fa",
        boxShadow: "2px 0 5px rgba(0, 0, 0, 0.1)",
        padding: isOpen ? "10px" : "5px", // Adjust padding for compact view
        position: "fixed",
        top: "60px", // Adjust 60px to match the header height
        left: 0,
        zIndex: 1040, // Ensure sidebar appears above other elements
        transition: "width 0.3s ease, padding 0.3s ease", // Smooth transition
        overflow: "hidden", // Prevent content overflow
      }}
      aria-label="Dashboard Sidebar"
    >
      {error && <div style={{ color: "red" }}>{error}</div>}
      {loading ? (
        <Spinner /> // Show spinner instead of loading message
      ) : (
        <>
          <h3
            style={{
              marginBottom: "10px",
              color: "#333",
              textAlign: "center",
              display: isOpen ? "block" : "none", // Hide when sidebar is collapsed
              fontSize: "14px", // Smaller font size
            }}
          >
            Dashboard
          </h3>
          <ul
            style={{
              listStyleType: "none",
              padding: 0,
              margin: 0,
            }}
          >
            {visibleRoutes.map(({ path, title }) => (
              <li
                key={path}
                style={{
                  marginBottom: "5px", // Reduced spacing between items
                  textAlign: isOpen ? "left" : "center", // Center text for compact view
                }}
              >
                <NavLink
                  to={path}
                  style={({ isActive }) => ({
                    textDecoration: "none",
                    color: isActive ? "#fff" : "#007bff",
                    backgroundColor: isActive ? "#007bff" : "transparent",
                    padding: "5px", // Minimal padding for compact view
                    borderRadius: "5px",
                    display: "block",
                    fontWeight: "500",
                    fontSize: isOpen ? "12px" : "10px", // Adjust font size for compact view
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    transition: "background-color 0.3s ease, color 0.3s ease",
                  })}
                  title={title} // Tooltip for compact view
                >
                  {isOpen ? title : title.charAt(0)} {/* Full text or initials */}
                </NavLink>
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
};

export default DashboardSidebar;
